import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { registerPushNotifications } from '../services/pushNotificationService';
import {
  clearWorkspaceSessionMeta,
  syncWorkspaceAccessFromResponse,
} from '../utils/workspaceSession';
import './AuthCallback.css';

const SEGMENT_COUNT = 12;

const AUTH_ERROR_MESSAGES = {
  unauthorized: 'Your email is not authorized to access this system. Please contact administrator.',
  server_error: 'Server error occurred. Please try again.',
  google_not_configured: 'Google sign-in is not configured. Please use email + OTP to log in.',
  no_token: 'Login session expired or invalid. Please try again.',
};

const COMPANY_HOST_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);

const resolvePostAuthRedirect = (requestedRedirect, userData) => {
  if (requestedRedirect && requestedRedirect !== '/') {
    return requestedRedirect;
  }

  const role = String(userData?.role || '');
  const hasCompany = Boolean(userData?.company?._id || userData?.company);
  if (COMPANY_HOST_ROLES.has(role) || hasCompany) {
    return '/host';
  }

  return '/';
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const error = searchParams.get('error');
  const redirect = searchParams.get('redirect') || localStorage.getItem('authRedirectPath') || '/';
  const [loadingMessage, setLoadingMessage] = useState('Authenticating...');

  useEffect(() => {
    if (error) {
      // Redirect to login with error so user sees message
      setTimeout(() => {
        navigate('/login?error=' + encodeURIComponent(error));
      }, 2000);
      return;
    }

    if (token) {
      clearWorkspaceSessionMeta();
      localStorage.removeItem('user');
      localStorage.setItem('token', token);

      const fetchUserData = async () => {
        try {
          setLoadingMessage('Loading your profile...');
          const response = await axios.get(
            getApiUrl('/api/auth/me'),
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          if (response.data.success) {
            const userData = response.data.user;
            const targetRedirect = resolvePostAuthRedirect(redirect, userData);
            
            localStorage.setItem('user', JSON.stringify(userData));
            syncWorkspaceAccessFromResponse(response.data);
            window.dispatchEvent(new CustomEvent('codex-authenticated'));
            registerPushNotifications({ requestPermission: true }).catch(() => {});
            
            // Determine redirect message
            let redirectMessage = 'home';
            if (targetRedirect === '/host') {
              redirectMessage = 'host dashboard';
            } else if (
              targetRedirect === '/meeting-book' ||
              targetRedirect === '/meeting-home' ||
              targetRedirect.startsWith('/meetings/')
            ) {
              redirectMessage = 'meeting booking';
            }
            
            // Clean up the stored redirect path
            localStorage.removeItem('authRedirectPath');
            
            setLoadingMessage(`Redirecting to ${redirectMessage}...`);
            
            // Redirect after short delay
            setTimeout(() => {
              navigate(targetRedirect);
              window.location.reload();
            }, 800);
          } else {
            // Invalid auth response - clear session and send user to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('authRedirectPath');
            setTimeout(() => {
              navigate('/login?error=no_token');
            }, 1000);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          // Token may be stale/invalid (e.g. user deleted). Clear and ask user to log in again.
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('authRedirectPath');
          setTimeout(() => {
            navigate('/login?error=no_token');
          }, 1000);
        }
      };
      
      fetchUserData();
    } else {
      // No token, redirect to login with error
      navigate('/login?error=no_token');
    }
  }, [token, error, navigate, redirect]);

  if (error) {
    const message = AUTH_ERROR_MESSAGES[error] || 'An error occurred during authentication. Please try again.';
    return (
      <div className="auth-callback">
        <motion.div
          className="callback-message error"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <h2>Authentication Failed</h2>
          <p>{message}</p>
          <p>Redirecting to login...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-callback">
      <motion.div
        className="callback-message success"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="auth-loading" aria-label="Loading">
          <div className="auth-spinner-segments">
            {Array.from({ length: SEGMENT_COUNT }, (_, i) => (
              <span
                key={i}
                className="auth-spinner-segment"
                style={{ '--i': i, '--n': SEGMENT_COUNT }}
              />
            ))}
          </div>
          <p className="auth-loading-text">{loadingMessage}</p>
          <div className="auth-progress-wrap">
            <div className="auth-progress-bar" />
          </div>
        </div>
        <h2>Authentication Successful!</h2>
      </motion.div>
    </div>
  );
};

export default AuthCallback;

